import { Vector3 } from 'three';

const BODY_HEIGHTS = [.20, .78, 1.38];
const WINDOW_RADIUS = .95;
const MAX_CUTAWAY = .92;
const CAMERA_CLEARANCE = .4;
const ACTOR_CLEARANCE = .6;
const finitePoint = point => point && Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z)
  && Math.abs(point.x) <= 1e6 && Math.abs(point.y) <= 1e6 && Math.abs(point.z) <= 1e6;
const smooth = (low, high, value) => {
  const t = Math.max(0, Math.min(1, (value - low) / (high - low)));
  return t * t * (3 - 2 * t);
};

// Numerical counterpart of the shader, useful for visibility policy checks.
// Actor position is the standing character's foot/root, not its head.
export function occlusionAt(point, eye, actor, enabled = true) {
  if (!enabled || !finitePoint(point) || !finitePoint(eye) || !finitePoint(actor)) return 0;
  let result = 0;
  for (const height of BODY_HEIGHTS) {
    const dx = actor.x - eye.x, dy = actor.y + height - eye.y, dz = actor.z - eye.z;
    const length = Math.hypot(dx, dy, dz);
    if (length <= CAMERA_CLEARANCE + ACTOR_CLEARANCE + .1) continue;
    const rx = dx / length, ry = dy / length, rz = dz / length;
    const px = point.x - eye.x, py = point.y - eye.y, pz = point.z - eye.z;
    const along = px * rx + py * ry + pz * rz;
    if (along <= CAMERA_CLEARANCE || along >= length - ACTOR_CLEARANCE) continue;
    const radial = Math.hypot(px - rx * along, py - ry * along, pz - rz * along);
    // A cone keeps the cutaway over the projected character window instead of
    // erasing a disproportionately large screen area close to the camera.
    const radius = .18 + (WINDOW_RADIUS - .18) * Math.max(0, Math.min(1, along / length));
    const roundEdge = 1 - smooth(radius * .58, radius, radial);
    const ends = smooth(CAMERA_CLEARANCE, CAMERA_CLEARANCE + .35, along)
      * (1 - smooth(length - ACTOR_CLEARANCE - .35, length - ACTOR_CLEARANCE, along));
    result = Math.max(result, roundEdge * ends * MAX_CUTAWAY);
  }
  return result;
}

// An ancestor identifies a compound palm/bush/rock. Explicit opt-out and
// protected actor/ecology/HUD ancestry always win, even under a marked parent.
function eligible(mesh) {
  let accepted = false;
  for (let object = mesh; object; object = object.parent) {
    const data = object.userData || {}, name = object.name || '';
    if (data.cameraOccluder === false || data.authoredFish || data.fishModelId || data.surfaceSchool || data.cameraOcclusionExclude)
      return false;
    if (/(?:fish|avatar|player|character|hud|water|ground|terrain|sea[_ -]?grass|kelp)/i.test(name)) return false;
    if (data.cameraOccluder === true || data.authoredPlant || /(?:palm|bush|rock)/i.test(name)) accepted = true;
  }
  return accepted;
}

const VERTEX_DECLARATION = '\nvarying vec3 vCameraCutawayWorld;\n';
const WORLD_POSITION = `
  // transformed already contains wind, morphing, skinning and displacement.
  vec4 cameraCutawayWorld = vec4(transformed, 1.0);
  #ifdef USE_BATCHING
    cameraCutawayWorld = batchingMatrix * cameraCutawayWorld;
  #endif
  #ifdef USE_INSTANCING
    cameraCutawayWorld = instanceMatrix * cameraCutawayWorld;
  #endif
  vCameraCutawayWorld = (modelMatrix * cameraCutawayWorld).xyz;
`;
const FRAGMENT_DECLARATION = `
  varying vec3 vCameraCutawayWorld;
  uniform vec3 uCameraCutawayEye;
  uniform vec3 uCameraCutawayActor;
  uniform float uCameraCutawayEnabled;
  float cameraCutawayRay(vec3 bodyTarget) {
    vec3 delta = bodyTarget - uCameraCutawayEye;
    float rayLength = length(delta);
    if (rayLength <= 1.1) return 0.0;
    vec3 rayDirection = delta / rayLength;
    vec3 offset = vCameraCutawayWorld - uCameraCutawayEye;
    float along = dot(offset, rayDirection);
    if (along <= .4 || along >= rayLength - .6) return 0.0;
    float radius = mix(.18, .95, clamp(along / rayLength, 0.0, 1.0));
    float radial = length(offset - rayDirection * along);
    float rounded = 1.0 - smoothstep(radius * .58, radius, radial);
    float ends = smoothstep(.4, .75, along) * (1.0 - smoothstep(rayLength - .95, rayLength - .6, along));
    return rounded * ends * .92;
  }
  float cameraCutawayWindow() {
    return max(cameraCutawayRay(uCameraCutawayActor + vec3(0.0,.20,0.0)),
      max(cameraCutawayRay(uCameraCutawayActor + vec3(0.0,.78,0.0)),
          cameraCutawayRay(uCameraCutawayActor + vec3(0.0,1.38,0.0))));
  }
  float cameraCutawayBayer2(vec2 p) {
    vec2 bit = mod(p, 2.0);
    return 2.0 * bit.x + 3.0 * bit.y - 4.0 * bit.x * bit.y;
  }
  float cameraCutawayDither() {
    vec2 cell = mod(floor(gl_FragCoord.xy), 4.0);
    return (4.0 * cameraCutawayBayer2(cell) + cameraCutawayBayer2(floor(cell / 2.0)) + .5) / 16.0;
  }
`;
const FRAGMENT_WINDOW = `
  if (uCameraCutawayEnabled > .5 && cameraCutawayWindow() > cameraCutawayDither()) discard;
`;

export function createCameraOcclusion() {
  const variants = new WeakMap(), ownedVariants = new WeakSet(), assignedMeshes = new WeakSet();
  const eyeUniform = { value: new Vector3() }, actorUniform = { value: new Vector3() }, enabledUniform = { value: 0 };
  const stats = { materials: 0, meshes: 0, updates: 0, enabled: false };

  function variantFor(original) {
    if (!original || !(original.isMeshStandardMaterial || original.isMeshBasicMaterial)) return original;
    if (original.userData?.surfaceSchool || original.userData?.fishModelId || original.userData?.authoredFish) return original;
    if (ownedVariants.has(original)) return original;
    if (variants.has(original)) return variants.get(original);
    const variant = original.clone(), beforeCompile = original.onBeforeCompile, programKey = original.customProgramCacheKey;
    // Material.clone preserves all render settings/textures but not JS hooks.
    variant.onBeforeCompile = function(shader, renderer) {
      beforeCompile.call(this, shader, renderer);
      // Supported standard/basic shaders retain these chunks. Do not damage a
      // custom shader replacement that has deliberately removed the pipeline.
      if (!shader.vertexShader.includes('#include <project_vertex>') || !shader.vertexShader.includes('#include <common>')
        || !shader.fragmentShader.includes('#include <common>') || !shader.fragmentShader.includes('#include <clipping_planes_fragment>')) return;
      shader.uniforms.uCameraCutawayEye = eyeUniform;
      shader.uniforms.uCameraCutawayActor = actorUniform;
      shader.uniforms.uCameraCutawayEnabled = enabledUniform;
      shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>' + VERTEX_DECLARATION)
        .replace('#include <project_vertex>', '#include <project_vertex>' + WORLD_POSITION);
      shader.fragmentShader = shader.fragmentShader.replace('#include <common>', '#include <common>' + FRAGMENT_DECLARATION)
        .replace('#include <clipping_planes_fragment>', '#include <clipping_planes_fragment>' + FRAGMENT_WINDOW);
    };
    variant.customProgramCacheKey = function() { return programKey.call(original) + ':camera-cutaway-v1'; };
    variant.userData.cameraCutaway = true;
    variants.set(original, variant); ownedVariants.add(variant); stats.materials++;
    return variant;
  }

  return {
    stats,
    isHitVisible(hit) {
      const materials = hit.object.material;
      const material = Array.isArray(materials) ? materials[hit.face?.materialIndex ?? 0] : materials;
      // Mostly cut-away surfaces must not steal taps from the scene visible
      // through the player window. The rest of each tree stays interactive.
      return !stats.enabled || !material?.userData.cameraCutaway
        || occlusionAt(hit.point, eyeUniform.value, actorUniform.value) <= .55;
    },
    apply(root) {
      if (!root?.traverse) return stats;
      root.traverse(mesh => {
        if (!mesh.isMesh || !eligible(mesh)) return;
        let changed = false;
        if (Array.isArray(mesh.material)) {
          const originals = mesh.material;
          for (let i = 0; i < originals.length; i++) {
            const variant = variantFor(originals[i]);
            if (variant !== originals[i]) { if (!changed) mesh.material = originals.slice(); mesh.material[i] = variant; changed = true; }
          }
        } else {
          const variant = variantFor(mesh.material);
          if (variant !== mesh.material) { mesh.material = variant; changed = true; }
        }
        if (changed && !assignedMeshes.has(mesh)) { assignedMeshes.add(mesh); stats.meshes++; }
      });
      return stats;
    },
    update(cameraPosition, actorPosition, enabled) {
      const active = !!enabled && !!finitePoint(cameraPosition) && !!finitePoint(actorPosition);
      if (active) {
        eyeUniform.value.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
        actorUniform.value.set(actorPosition.x, actorPosition.y, actorPosition.z);
      }
      enabledUniform.value = active ? 1 : 0;
      stats.enabled = active; stats.updates++;
    },
  };
}
